// @flow

import { shouldIgnoreAddress, shouldIgnorePhoneNumber } from '@tectonics/prs/ui';
import {
  getSafeCreditCardPaymentOption,
  getSafeDebitCardPaymentOption,
} from '@tectonics/prs/incoming-payment/payment-option';
import { type PaymentSpecGenerator } from '@tectonics/prs/payment-spec/generator';

import {
  isLocalPayment,
  shouldHideCustomerEmailField,
  getSafeUi,
  needsCaptcha,
} from '@tectonics/prs/incoming-payment';
import { getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment/get-payment-options';

const paymentSpecGenerator: PaymentSpecGenerator = {

  features: ({ incomingPayment }) => {
    const features = [
      { name: 'display_merchant_logo', traits: [] },
      { name: 'display_receipt_original_amount', traits: [] },
      ...(isLocalPayment(incomingPayment) ? [] : [{ name: 'display_receipt_fx_rate', traits: [] }]),
      { name: 'display_receipt_final_amount', traits: [] },
    ];

    switch (incomingPayment.status) {
      case 'OP': {
        const ui = getSafeUi(incomingPayment);

        return [
          ...features,
          { name: 'field_panamanian_document', traits: [] },
          { name: 'field_customer_name', traits: [] },
          ...(shouldHideCustomerEmailField(incomingPayment) ? [] : [{ name: 'field_customer_email', traits: [] }]),
          ...(shouldIgnorePhoneNumber(ui) ? [] : [{ name: 'field_panamanian_phone_number', traits: [] }]),
          ...(needsCaptcha(incomingPayment) ? [{ name: 'field_captcha_token', traits: [] }] : []),
          ...(needsCaptcha(incomingPayment) ? [{ name: 'field_captcha_token', traits: [] }] : []),
          { name: 'display_back_button', traits: [] },
        ];
      }

      default:
        return features;
    }
  },

  creditcard: ({ incomingPayment }) => {
    const features = [
      { name: 'display_selected_instalment', traits: [] },
    ];

    switch (incomingPayment.status) {
      case 'OP': {
        const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
        const brands = getSafeCreditCardPaymentOption(paymentOptions);
        const ui = getSafeUi(incomingPayment);

        return [
          ...features,
          { name: 'field_card_holder_name', traits: [] },
          { name: 'field_card_number', traits: [] },
          { name: 'field_card_expiry_date', traits: [] },
          { name: 'field_card_security_code', traits: [] },
          { name: 'field_card_instalments', traits: [] },
          { name: 'display_card_accepts_brand', traits: brands },
          { name: 'display_alternative_card_accepts_brand', traits: brands },
          ...(shouldIgnoreAddress(ui) ? [] : [
            { name: 'field_panamanian_zipcode', traits: [] },
            { name: 'field_panamanian_state', traits: [] },
            { name: 'field_panamanian_city', traits: [] },
            { name: 'field_panamanian_address', traits: [] },
            { name: 'field_panamanian_street_number', traits: [] },
            { name: 'field_panamanian_street_complement', traits: [] },
          ]),
        ];
      }

      default:
        return features;
    }
  },
  debitcard: ({ incomingPayment }) => {
    const features = [
      { name: 'display_selected_instalment', traits: [] },
    ];

    switch (incomingPayment.status) {
      case 'OP': {
        const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
        const brands = getSafeDebitCardPaymentOption(paymentOptions);
        const ui = getSafeUi(incomingPayment);

        return [
          ...features,
          { name: 'field_card_holder_name', traits: [] },
          { name: 'field_card_number', traits: [] },
          { name: 'field_card_expiry_date', traits: [] },
          { name: 'field_card_security_code', traits: [] },
          { name: 'display_debit_card_accepts_brand', traits: brands },
          ...(shouldIgnoreAddress(ui) ? [] : [
            { name: 'field_panamanian_zipcode', traits: [] },
            { name: 'field_panamanian_state', traits: [] },
            { name: 'field_panamanian_city', traits: [] },
            { name: 'field_panamanian_address', traits: [] },
            { name: 'field_panamanian_street_number', traits: [] },
            { name: 'field_panamanian_street_complement', traits: [] },
          ]),
        ];
      }

      default:
        return features;
    }
  },
  cash: ({ incomingPayment, lookAndFeelCode }) => {
    return [
      ...(shouldHideCustomerEmailField(incomingPayment) ? [] : [{ name: 'field_customer_email', traits: [] }]),

      { name: 'display_cash_total_value', traits: [] },
      { name: 'display_cash_due_date', traits: [] },
      { name: 'display_cash_barcode', traits: [] },
      { name: 'display_cash_number', traits: [] },
      { name: 'action_cash_download', traits: [] },
      { name: 'action_cash_save_pdf', traits: [] },
      { name: 'display_cash_email_warning', traits: [] },
      { name: 'display_cash_instructions', traits: [] },
      { name: 'display_cash_tips', traits: [] },
      { name: 'display_cash_total_amount', traits: [] },
      { name: 'display_cash_duedate', traits: [] },
    ];
  },
};

export default paymentSpecGenerator;
